// src/pages/Preferences.tsx
//
// Personal account settings, available to every signed-in role. This is
// deliberately separate from /settings, which is the admin console for
// org-wide configuration — regular users have no access there.
//
// Everything on this page writes to the signed-in user's own document.
// Firestore rules allow a self-write of only displayName, avatarUrl,
// notifications, notificationsLastReadAt, and updatedAt, so nothing here
// may touch role, email, department, or status — those are shown
// read-only and changed by an admin.

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { updateMyProfile, updateMyNotifications, uploadAvatar } from "@/lib/firestore";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { Avatar, Spinner, Toggle, toast } from "@/components/shared";
import { NotificationPreferences } from "@/types";

const MAX_AVATAR_BYTES = 2 * 1024 * 1024; // must match storage.rules

function Card({ title, subtitle, icon, iconBg, iconColor, children }: {
  title: string; subtitle: string; icon: string; iconBg: string; iconColor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="card" style={{ marginBottom: 14, overflow: "hidden" }}>
      <div className="card-hdr" style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: iconBg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill={iconColor} aria-hidden="true"><path d={icon} /></svg>
        </div>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
          <div style={{ fontSize: 12, color: "var(--text2)" }}>{subtitle}</div>
        </div>
      </div>
      <div className="card-body">{children}</div>
    </div>
  );
}

function PrefRow({ title, subtitle, value, onChange, disabled }: {
  title: string; subtitle: string; value: boolean; onChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 4px", borderTop: "0.5px solid var(--border)" }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 500 }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 2, lineHeight: 1.5 }}>{subtitle}</div>
      </div>
      {disabled
        ? <span className="badge" style={{ background: "var(--gray)", color: "var(--text2)", fontSize: 10 }}>always on</span>
        : <Toggle value={value} onChange={onChange} />}
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div className="fg">
      <label>{label}</label>
      <div style={{ fontSize: 13, padding: "9px 0", color: "var(--text2)" }}>{value || "—"}</div>
    </div>
  );
}

export default function Preferences() {
  useDocumentTitle("Preferences");
  const { firebaseUser, helpDeskUser, sendPasswordReset } = useAuth();
  const isAgent = helpDeskUser?.role === "agent" || helpDeskUser?.role === "admin";

  const [displayName, setDisplayName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>(undefined);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [sendingReset, setSendingReset] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPreferences>({ dailyReport: false, weeklyReport: false });
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!helpDeskUser) return;
    setDisplayName(helpDeskUser.displayName ?? "");
    setAvatarUrl(helpDeskUser.avatarUrl);
    setPrefs({
      dailyReport: !!helpDeskUser.notifications?.dailyReport,
      weeklyReport: !!helpDeskUser.notifications?.weeklyReport,
      // Opt-out fields: anything not explicitly false is on.
      ticketConfirmation: helpDeskUser.notifications?.ticketConfirmation !== false,
      ticketReplies:      helpDeskUser.notifications?.ticketReplies !== false,
      csatSurvey:         helpDeskUser.notifications?.csatSurvey !== false,
      agentAssignment:    helpDeskUser.notifications?.agentAssignment !== false,
    });
  }, [helpDeskUser]);

  const saveProfile = async () => {
    if (!firebaseUser) return;
    const name = displayName.trim();
    if (!name) { toast("Display name can't be empty", "error"); return; }
    setSavingProfile(true);
    try {
      await updateMyProfile(firebaseUser.uid, { displayName: name });
      toast("Profile saved");
    } catch (e) {
      toast(`Failed to save: ${(e as Error).message}`, "error");
    }
    setSavingProfile(false);
  };

  const onPickAvatar = async (file: File) => {
    if (!firebaseUser) return;
    if (!file.type.startsWith("image/")) { toast("Choose an image file", "error"); return; }
    if (file.size > MAX_AVATAR_BYTES) { toast("Image must be under 2 MB", "error"); return; }
    setUploading(true);
    try {
      const url = await uploadAvatar(firebaseUser.uid, file);
      await updateMyProfile(firebaseUser.uid, { avatarUrl: url });
      setAvatarUrl(url);
      toast("Photo updated");
    } catch (e) {
      toast(`Upload failed: ${(e as Error).message}`, "error");
    }
    setUploading(false);
  };

  const removeAvatar = async () => {
    if (!firebaseUser) return;
    setUploading(true);
    try {
      // Clear the pointer rather than deleting the object — other places
      // may still be rendering a cached URL, and the file is harmless.
      await updateMyProfile(firebaseUser.uid, { avatarUrl: "" });
      setAvatarUrl("");
      toast("Photo removed");
    } catch (e) {
      toast(`Failed: ${(e as Error).message}`, "error");
    }
    setUploading(false);
  };

  // Notification toggles save immediately — there's no Save button to miss.
  const setPref = async (patch: Partial<NotificationPreferences>) => {
    if (!firebaseUser) return;
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      await updateMyNotifications(firebaseUser.uid, next);
    } catch (e) {
      setPrefs(prefs); // roll back the optimistic flip
      toast(`Failed to save: ${(e as Error).message}`, "error");
    }
  };

  const handlePasswordReset = async () => {
    if (!helpDeskUser?.email) return;
    setSendingReset(true);
    try {
      await sendPasswordReset(helpDeskUser.email);
      toast(`Reset link sent to ${helpDeskUser.email}`);
    } catch (e) {
      toast(`Failed: ${(e as Error).message}`, "error");
    }
    setSendingReset(false);
  };

  if (!helpDeskUser) {
    return <div style={{ padding: 20 }}><Spinner /></div>;
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: 20 }}>
      <h1 style={{ marginBottom: 3 }}>Preferences</h1>
      <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 20 }}>
        Your profile and the emails you receive
      </p>

      <div style={{ maxWidth: 720 }}>
        <Card title="Profile" subtitle="How you appear on tickets and in the queue"
          icon="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
          iconBg="var(--blue-l)" iconColor="var(--blue)"
        >
          <div style={{ display: "flex", gap: 18, alignItems: "flex-start", marginBottom: 4 }}>
            <div style={{ textAlign: "center", flexShrink: 0 }}>
              <Avatar name={displayName || helpDeskUser.email} url={avatarUrl || undefined} size={72} />
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 10 }}>
                <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }}
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickAvatar(f); e.target.value = ""; }} />
                <button className="btn btn-secondary btn-sm" disabled={uploading}
                  onClick={() => fileRef.current?.click()}>
                  {uploading ? <Spinner size={12} color="var(--text2)" /> : avatarUrl ? "Change" : "Upload"}
                </button>
                {avatarUrl && (
                  <button className="btn btn-secondary btn-sm" disabled={uploading} onClick={removeAvatar}>Remove</button>
                )}
              </div>
              <div style={{ fontSize: 10, color: "var(--text3)", marginTop: 6 }}>JPG or PNG<br />Max 2 MB</div>
            </div>

            <div style={{ flex: 1 }}>
              <div className="fg">
                <label htmlFor="pref-display-name">Display name</label>
                <input id="pref-display-name" className="fi" value={displayName} maxLength={120}
                  onChange={(e) => setDisplayName(e.target.value)} />
              </div>
              <div className="frow">
                <ReadOnlyField label="Email" value={helpDeskUser.email} />
                <ReadOnlyField label="Department" value={helpDeskUser.department} />
              </div>
              <div style={{ fontSize: 11, color: "var(--text2)", marginTop: -4, marginBottom: 12 }}>
                Email and department are managed by your administrator.
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <button className="btn btn-primary btn-sm" onClick={saveProfile} disabled={savingProfile}>
                  {savingProfile ? <Spinner size={12} color="white" /> : "Save"}
                </button>
              </div>
            </div>
          </div>
        </Card>

        <Card title="Email notifications" subtitle="Which emails the helpdesk sends you"
          icon="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"
          iconBg="var(--orange-l)" iconColor="var(--orange-t)"
        >
          <PrefRow
            title="Ticket received"
            subtitle="A confirmation when you submit a ticket, with its reference number."
            value={prefs.ticketConfirmation !== false}
            onChange={(v) => setPref({ ticketConfirmation: v })}
          />
          <PrefRow
            title="Replies on my tickets"
            subtitle="When an agent responds. Turning this off means you'll only see replies by opening the ticket."
            value={prefs.ticketReplies !== false}
            onChange={(v) => setPref({ ticketReplies: v })}
          />
          <PrefRow
            title="Satisfaction survey"
            subtitle="A short rating request after one of your tickets is resolved."
            value={prefs.csatSurvey !== false}
            onChange={(v) => setPref({ csatSurvey: v })}
          />
          <PrefRow
            title="Requests for more information"
            subtitle="When we need more detail before we can start work. Always sent — without it your ticket would sit waiting with no way to know."
            value
            onChange={() => {}}
            disabled
          />
          {isAgent && (
            <PrefRow
              title="Ticket assigned to me"
              subtitle="When another agent or an admin assigns you a ticket."
              value={prefs.agentAssignment !== false}
              onChange={(v) => setPref({ agentAssignment: v })}
            />
          )}
          <div style={{ fontSize: 11, color: "var(--text3)", marginTop: 12, fontStyle: "italic" }}>
            Your administrator can disable some of these helpdesk-wide, in which case they won't
            be sent even when switched on here.
          </div>
        </Card>

        {isAgent && (
          <Card title="Summary reports" subtitle="AI-written recaps of your queue"
            icon="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"
            iconBg="var(--gem-l)" iconColor="var(--gem)"
          >
            <div style={{ fontSize: 12, color: "var(--text2)", padding: "0 4px 4px", lineHeight: 1.5 }}>
              {helpDeskUser.role === "admin"
                ? "As an admin you receive an organization-wide summary covering every agent's work, SLA performance, CSAT scores, and deflections."
                : "A personalized summary of your own tickets, SLA performance, CSAT scores, and what's still in flight."}
            </div>
            <PrefRow
              title="Daily report"
              subtitle="Weekday evenings at 6 PM (helpdesk timezone). Skipped on days with no activity."
              value={!!prefs.dailyReport}
              onChange={(v) => setPref({ dailyReport: v })}
            />
            <PrefRow
              title="Weekly report"
              subtitle="Fridays at 6 PM, covering the previous 7 days."
              value={!!prefs.weeklyReport}
              onChange={(v) => setPref({ weeklyReport: v })}
            />
          </Card>
        )}

        <Card title="Security" subtitle="Your sign-in credentials"
          icon="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM9 6c0-1.66 1.34-3 3-3s3 1.34 3 3v2H9V6z"
          iconBg="var(--green-l)" iconColor="var(--green-t)"
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>Password</div>
              <div style={{ fontSize: 11, color: "var(--text2)", marginTop: 2, lineHeight: 1.5 }}>
                We'll email {helpDeskUser.email} a link to set a new one. If you sign in with
                Google, you can ignore this.
              </div>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={handlePasswordReset} disabled={sendingReset}>
              {sendingReset ? <Spinner size={12} color="var(--text2)" /> : "Send reset link"}
            </button>
          </div>
        </Card>
      </div>
    </div>
  );
}
